import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Checkbox,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  List,
  ListItemText,
  ListItemButton,
} from '@mui/material';
import { CheckCircle } from '@mui/icons-material';
import { UserEquipment, EQUIPMENT_TYPE_LABELS } from '../types/Equipment';
import { Dive } from '../types/Dive';
import { diveService as accountDiveService } from '../services/diveService';
import { equipmentService } from '../services/equipmentService';
import { preferencesManager } from '../utils/preferencesManager';
import EquipmentTypeIcon from './EquipmentTypeIcon';

interface BulkAssignDivesDialogProps {
  open: boolean;
  selectedEquipment: UserEquipment[];
  onClose: () => void;
  onAssigned: () => void;
}

const BulkAssignDivesDialog: React.FC<BulkAssignDivesDialogProps> = ({
  open,
  selectedEquipment,
  onClose,
  onAssigned,
}) => {
  const [dives, setDives] = useState<Dive[]>([]);
  const [selectedDiveIds, setSelectedDiveIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      loadDives();
      setSelectedDiveIds(new Set());
      setError(null);
    }
  }, [open]);

  const loadDives = async () => {
    try {
      setLoading(true);

      // Load dives from user's account (DynamoDB)
      const allDives = await accountDiveService.getMyDives();

      // Sort dives by date, most recent first
      const sortedDives = allDives.sort((a, b) =>
        new Date(b.startTime).getTime() - new Date(a.startTime).getTime()
      );
      setDives(sortedDives);
    } catch (error: any) {
      console.error('Failed to load dives for equipment assignment:', error);

      // Provide more specific error messages
      if (error.message?.includes('Failed to fetch dives')) {
        setError('Failed to load dives. Please ensure you are logged in and have synced your dives.');
      } else if (error.message?.includes('authentication') || error.message?.includes('Unauthorized')) {
        setError('Your session has expired. Please log in again.');
      } else {
        setError(`Failed to load dives: ${error.message || 'Unknown error'}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleToggleDive = (diveId: string) => {
    setSelectedDiveIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(diveId)) {
        newSet.delete(diveId);
      } else {
        newSet.add(diveId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedDiveIds.size === dives.length) {
      setSelectedDiveIds(new Set());
    } else {
      setSelectedDiveIds(new Set(dives.map(d => d.id)));
    }
  };

  const handleAssign = async () => {
    if (selectedDiveIds.size === 0) {
      setError('Please select at least one dive');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const equipmentIds = selectedEquipment.map(eq => eq.equipmentId);

      // Update each selected dive with the equipment
      for (const diveId of Array.from(selectedDiveIds)) {
        const dive = dives.find(d => d.id === diveId);
        if (!dive) continue;

        // Get current equipment IDs for this dive
        const currentEquipmentIds = dive.additionalData?.equipmentIds || [];

        // Merge with selected equipment (avoid duplicates)
        const mergedEquipmentIds = Array.from(
          new Set([...currentEquipmentIds, ...equipmentIds])
        );

        // Update the dive using the account dive service
        // Note: API expects equipmentIds at root level, not in additionalData
        await accountDiveService.updateDive(diveId, {
          equipmentIds: mergedEquipmentIds,
        });
      }

      // Recalculate equipment dive counts after bulk assignment
      const userId = preferencesManager.getUserId();
      if (userId) {
        try {
          console.log('Recalculating equipment dive counts...');
          await equipmentService.recalculateDiveCounts(userId);
          console.log('Equipment dive counts recalculated successfully');
        } catch (recalcError) {
          console.error('Failed to recalculate dive counts:', recalcError);
          // Don't fail the whole operation if recalculation fails
        }
      }

      onAssigned();
      handleClose();
    } catch (error) {
      console.error('Failed to assign equipment to dives:', error);
      setError('Failed to assign equipment. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setSelectedDiveIds(new Set());
    setError(null);
    onClose();
  };

  const getDiveEquipmentCount = (dive: Dive): number => {
    return dive.additionalData?.equipmentIds?.length || 0;
  };

  const formatLocation = (dive: Dive): string => {
    // If location is empty, try to create a descriptive label
    if (!dive.location || dive.location === 'Unknown Location') {
      // If we have coordinates, show a simplified version
      if (dive.latitude && dive.longitude) {
        return `Dive at ${dive.latitude.toFixed(4)}°, ${dive.longitude.toFixed(4)}°`;
      }
      return 'Unknown Location';
    }

    // If location is in GPS coordinate format (contains ° and ,), simplify it
    if (dive.location.includes('°') && dive.location.includes(',')) {
      // Extract just the numeric parts for a cleaner display
      if (dive.latitude && dive.longitude) {
        return `Dive at ${dive.latitude.toFixed(4)}°, ${dive.longitude.toFixed(4)}°`;
      }
    }

    // Otherwise, return the location name as-is
    return dive.location;
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Assign Equipment to Dives
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          {/* Selected Equipment Summary */}
          <Box sx={{ mb: 3, p: 2, backgroundColor: 'action.hover', borderRadius: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
              Selected Equipment ({selectedEquipment.length})
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {selectedEquipment.map(eq => {
                const label = eq.brand || EQUIPMENT_TYPE_LABELS[eq.equipmentType as keyof typeof EQUIPMENT_TYPE_LABELS];
                return (
                  <Chip
                    key={eq.equipmentId}
                    icon={<EquipmentTypeIcon type={eq.equipmentType} size={18} framed={false} />}
                    label={label}
                    size="small"
                    variant="outlined"
                  />
                );
              })}
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Select Dives Section */}
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
              Select Dives ({selectedDiveIds.size} selected)
            </Typography>
            <Button
              size="small"
              onClick={handleSelectAll}
              variant="outlined"
            >
              {selectedDiveIds.size === dives.length ? 'Deselect All' : 'Select All'}
            </Button>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" py={4}>
              <CircularProgress />
            </Box>
          ) : dives.length === 0 ? (
            <Alert severity="info">
              No dives found. Import some dives from Suunto first.
            </Alert>
          ) : (
            <List sx={{ maxHeight: '400px', overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              {dives.map(dive => {
                const isSelected = selectedDiveIds.has(dive.id);
                const equipmentCount = getDiveEquipmentCount(dive);
                const date = new Date(dive.startTime).toLocaleDateString();

                return (
                  <ListItemButton
                    key={dive.id}
                    onClick={() => handleToggleDive(dive.id)}
                    selected={isSelected}
                    sx={{
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      '&:last-child': { borderBottom: 'none' },
                    }}
                  >
                    <Checkbox
                      edge="start"
                      checked={isSelected}
                      tabIndex={-1}
                      disableRipple
                    />
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                            {formatLocation(dive)}
                          </Typography>
                          {equipmentCount > 0 && (
                            <Chip
                              label={`${equipmentCount} gear`}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: '0.7rem' }}
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="caption" display="block">
                            {date} • {dive.maxDepth}m • {dive.duration}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {dive.diveType}
                          </Typography>
                        </Box>
                      }
                    />
                    {isSelected && (
                      <CheckCircle color="primary" sx={{ ml: 1 }} />
                    )}
                  </ListItemButton>
                );
              })}
            </List>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleAssign}
          variant="contained"
          disabled={saving || selectedDiveIds.size === 0 || selectedEquipment.length === 0}
          startIcon={saving ? <CircularProgress size={16} /> : null}
        >
          {saving ? 'Assigning...' : `Assign to ${selectedDiveIds.size} Dive${selectedDiveIds.size !== 1 ? 's' : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BulkAssignDivesDialog;
